import { averageCents, totalsByCurrency, type CurrencyTotal } from '../../platform/money/totals'
import {
  amountChange,
  daysBetween,
  formatDue,
  onTime,
  relativeDue,
  settledAs,
  type AmountChange,
  type LocalDate,
} from './recurringRules'
import type { Bill, Frequency, Period } from './recurringWire'
import type { MoneyByCurrency } from './useRecurring'

/**
 * The detail screen's figures and words (M7-R10–R12, D12), as pure functions
 * of the bill and its payments timeline. Every rule here REUSES Task 2's and
 * Task 4's: `onTime` and `settledAs` (S6) and `amountChange` (D3) are called,
 * never re-derived, so the list's "Increased $50" and this screen's Trend
 * cannot disagree about the same two payments.
 */

export type DetailStats = {
  /** Payments whose period fell due in the last 12 months, per currency (never summed across). */
  paid12m: MoneyByCurrency
  /** The average of those payments; null when there are none or they mix currencies. */
  average: CurrencyTotal | null
  /** S6 over the whole loaded timeline: paid late is settled but NOT on time; a period not yet due is not settled. */
  onTime: { onTime: number; settled: number }
  /** D12: D3's rule, shown as a signed percentage. */
  trend: AmountChange | null
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** `today` one calendar year back, Feb 29 clamped to the 28th. The window is AFTER this date. */
function yearBefore(today: LocalDate): LocalDate {
  const [y = 0, m = 1, d = 1] = today.split('-').map(Number)
  const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate()
  return `${y - 1}-${pad(m)}-${pad(Math.min(d, last))}`
}

/**
 * `currency` is the bill's: a payment carries no currency of its own on the
 * wire (`RecurringPaymentResponse`), so every payment is in the bill's. The
 * per-currency shape is kept anyway — `totalsByCurrency` is the only sanctioned
 * way to sum money here — and `average` is null unless there is exactly one.
 */
export function detailStats(periods: Period[], currency: string, today: LocalDate): DetailStats {
  const from = yearBefore(today)
  const payments = periods.flatMap((p) =>
    p.status === 'paid' && p.dueDate > from && p.payment?.amountCents != null ? [p.payment.amountCents] : [],
  )
  const paid12m = totalsByCurrency(payments, (c) => c, () => currency)
  const only = paid12m.length === 1 ? paid12m[0] : undefined
  return {
    paid12m,
    average: only ? { currency: only.currency, cents: averageCents(only.cents, payments.length) } : null,
    // S17: the server stamps `missed` by its UTC day, so on the due date's
    // evening west of UTC a period can arrive `missed` while the history row
    // (and the user's calendar) still says Upcoming. Not settled until it is due.
    onTime: onTime(periods.filter((p) => !(p.status === 'missed' && p.dueDate >= today))),
    trend: amountChange(periods),
  }
}

export type HistoryTone = 'paid' | 'late' | 'missed' | 'neutral'

/**
 * A history row's state, as a WORD first (R12: Paid late is text, not a
 * colour). `missed` from the server is dated by the host's UTC day (S17), so a
 * period due today or later is still Upcoming for a user west of UTC. The
 * period the bill still owes — `nextDue`, past due — is Overdue, the hero's
 * word for it; an older unpaid one the server has moved past is Missed.
 */
export function historyState(p: Period, today: LocalDate, nextDue: LocalDate | null = null): { tone: HistoryTone; text: string } {
  const settled = settledAs(p)
  if (settled === 'paid' && p.payment) return { tone: 'paid', text: `Paid ${formatDue(p.payment.paidOn, 'short')}` }
  if (settled === 'paid_late' && p.payment) return { tone: 'late', text: `Paid late · ${formatDue(p.payment.paidOn, 'short')}` }
  switch (p.status) {
    case 'paid':
      // An optimistic cash flip: paid, with the payment row still on its way.
      return { tone: 'paid', text: 'Paid' }
    case 'missed':
    case 'unknown':
      if (p.dueDate >= today) return { tone: 'neutral', text: 'Upcoming' }
      // P2 counts both as Overdue: the period the bill still owes says what the hero says.
      if (p.dueDate === nextDue) return { tone: 'missed', text: 'Overdue' }
      return p.status === 'missed' ? { tone: 'missed', text: 'Missed' } : { tone: 'neutral', text: 'Not confirmed' }
    case 'skipped':
      return { tone: 'neutral', text: 'Skipped' }
    case 'paused':
      return { tone: 'neutral', text: 'Paused' }
    case 'upcoming':
      return { tone: 'neutral', text: 'Upcoming' }
  }
}

/** More than one period a month: a month name alone would label two rows the same. */
const SUB_MONTHLY: readonly Frequency[] = ['weekly', 'bi_weekly', 'semi_monthly', 'unknown']

export function historyLabel(p: Period, frequency: Frequency): string {
  if (SUB_MONTHLY.includes(frequency)) return formatDue(p.dueDate, 'long')
  // "22 July 2026" → "July 2026".
  return formatDue(p.dueDate, 'long').replace(/^\d+ /, '')
}

/** "14 months": oldest to newest period, calendar months inclusive. */
export function historySpan(periods: Period[]): string {
  const dates = periods.map((p) => p.dueDate).sort()
  const first = dates[0]
  const last = dates.at(-1)
  if (first === undefined || last === undefined) return '0 months'
  const months = (Number(last.slice(0, 4)) - Number(first.slice(0, 4))) * 12 + Number(last.slice(5, 7)) - Number(first.slice(5, 7)) + 1
  return `${months} ${months === 1 ? 'month' : 'months'}`
}

export type HeroStatus = { kind: 'overdue' | 'due' | 'paid' | 'paused' | 'inactive' | 'none'; text: string }

const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`

/**
 * The hero's status line (R10). `nextDue` is the period the server will pay
 * next (Ruling 34), so it is also the oldest one owed: past it, the bill is
 * overdue by exactly that many local calendar days.
 *
 * Ruling 40: **the detail describes the bill; the list describes the month.**
 * The month view's P3 floor (S7: an overdue period shows only in its own
 * month, and only the current month feeds the badge) is a rule about a MONTH.
 * This screen is about one bill, and a bill whose next payment is late is late
 * whatever month it fell due in — so no month floor here, by design.
 *
 * "Paid · next due" when the period just before `nextDue` is paid; otherwise
 * the distance to it.
 */
export function heroStatus(bill: Bill, periods: Period[], today: LocalDate): HeroStatus {
  if (bill.isPaused) return { kind: 'paused', text: 'Paused' }
  if (!bill.isActive) return { kind: 'inactive', text: 'Inactive' }
  const next = bill.nextDue
  if (next === null) return { kind: 'none', text: 'No upcoming payment' }
  const due = formatDue(next, 'long')
  const rel = relativeDue(next, today)
  if (rel.kind === 'late') return { kind: 'overdue', text: `Overdue by ${days(daysBetween(next, today))} · was due ${due}` }
  const before = periods.filter((p) => p.dueDate < next).sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1))[0]
  if (before?.status === 'paid') return { kind: 'paid', text: `Paid · next due ${due}` }
  return { kind: 'due', text: rel.kind === 'today' ? `Due today · ${due}` : `Due in ${days(rel.days)} · ${due}` }
}
